export function buildPragmaticCancellationParams(bonusCode, clientCode) {
  const isScalar = bonusCode != null && !["object", "function"].includes(typeof bonusCode);
  const normalizedBonusCode = isScalar ? String(bonusCode).trim() : "";
  if (!normalizedBonusCode) throw new Error("Bonus code is required to cancel a Pragmatic campaign.");

  return {
    clientCode: clientCode,
    bonusCode: normalizedBonusCode
  };
}

export function resolveSelectedPlayer(players, selectedPlayerId) {
  const normalizedId = selectedPlayerId == null ? "" : String(selectedPlayerId).trim();
  const player = normalizedId
    ? (Array.isArray(players) ? players : []).find(
        candidate =>
          (candidate?.id != null && String(candidate.id).trim() === normalizedId) ||
          (candidate?.username != null && String(candidate.username).trim() === normalizedId),
      ) ?? null
    : null;

  return {
    player,
    route: player ? "player" : "general",
    clientCode: player?.clientcode
  };
}

export function normalizeFreeSpinType(value) {
  if (value == null || (typeof value === "string" && value.trim() === "")) {
    return null;
  }
  if (typeof value !== "string" || value.trim() !== "F") {
    throw new Error('frType must be empty or "F".');
  }
  return "F";
}


function hasValue(value) {
  return value != null && String(value).trim() !== "";
}

const PRAGMATIC_BONUS_CODE_ALPHABET = "ABCDEFGHIJKLMNOPQRSTUVWXYZ0123456789";

export function generatePragmaticBonusCode(clientCode, random = Math.random) {
  const normalizedClientCode = String(clientCode ?? "")
    .trim()
    .toUpperCase()
    .replace(/[^A-Z0-9]/g, "");
  if (!normalizedClientCode) {
    throw new Error("clientCode is required to generate the bonus code.");
  }

  const randomSuffix = Array.from({ length: 12 }, () => {
    const index = Math.floor(random() * PRAGMATIC_BONUS_CODE_ALPHABET.length);
    return PRAGMATIC_BONUS_CODE_ALPHABET[index];
  }).join("");

  return `PRAG${normalizedClientCode}${randomSuffix}`;
}

export function validatePragmaticFreeSpinFields({ bonusCode, startDate, rounds, frType, assignationMode } = {}) {
  const errors = [];
  if (!hasValue(bonusCode)) errors.push("bonusCode");
  else if (!/^[A-Za-z0-9]+$/.test(String(bonusCode).trim())) errors.push("bonusCodeFormat");
  if (!hasValue(startDate)) errors.push("startDate");
  if (!hasValue(assignationMode)) errors.push("assignationMode");
  if (hasValue(rounds) && (!/^\d+$/.test(String(rounds).trim()) || Number(rounds) <= 0)) {
    errors.push("roundsInvalid");
  }
  if (hasValue(rounds) && hasValue(frType)) errors.push("roundsAndFrType");
  if (!hasValue(rounds) && !hasValue(frType)) errors.push("awardConfiguration");
  return errors;
}
export function buildPragmaticFreeSpinRequest({
  form,
  games,
  players,
  selectedPlayerId,
  clientCode,
  startDate,
  expirationDate,
  validityDate,
  campaignEndDate,
  expirationPeriod,
  validityPeriod,
  validityMode = "dates"
}) {
  const selection = resolveSelectedPlayer(players, selectedPlayerId);
  const hasSelectedPlayerId = selectedPlayerId != null && String(selectedPlayerId).trim() !== "";
  if (hasSelectedPlayerId && !selection.player) {
    throw new Error("The selected player was not found.");
  }

  const fieldErrors = validatePragmaticFreeSpinFields({ ...form, startDate });
  if (fieldErrors.includes("bonusCode")) throw new Error("bonusCode is required.");
  if (fieldErrors.includes("bonusCodeFormat")) throw new Error("bonusCode must contain only letters and numbers.");
  if (fieldErrors.includes("startDate")) throw new Error("startDate is required.");
  if (fieldErrors.includes("roundsInvalid")) throw new Error("rounds must be greater than zero.");
  if (fieldErrors.includes("roundsAndFrType")) throw new Error("rounds and frType must not be used together.");

  const frType = normalizeFreeSpinType(form.frType);
  let currency = typeof form.currency === "string"
    ? form.currency.trim().toUpperCase()
    : "";

  if (!currency) throw new Error("currency is required.");

  const pragmaticCreateFRB = {
    bonusCode: String(form.bonusCode).trim(),
    clientCode: clientCode,
    assignationMode: form.assignationMode,
    currency,
    startDate
  };

  if (selection.player || validityMode === "dates") {
    if (hasValue(expirationDate)) pragmaticCreateFRB.expirationDate = expirationDate;
    if (hasValue(validityDate)) pragmaticCreateFRB.validityDate = validityDate;
  } else {
    if (hasValue(campaignEndDate)) pragmaticCreateFRB.campaignEndDate = campaignEndDate;
    if (hasValue(expirationPeriod)) pragmaticCreateFRB.expirationPeriod = Number(expirationPeriod);
    if (hasValue(validityPeriod)) pragmaticCreateFRB.validityPeriod = Number(validityPeriod);
  }
  if (hasValue(form.rounds)) pragmaticCreateFRB.rounds = form.rounds;

  if (frType) {
    pragmaticCreateFRB.frType = frType;
  }

  if (selection.player) {
    pragmaticCreateFRB.playerId = selection.player.id;
  }
  let playerInfo;
  if (selection.player){
     playerInfo = {playerId: selection.player.id, playerName: selection.player.username_origin, playerStatus: 1};
  }

  return {
    route: selection.route,
    clientCode: clientCode,
    payload: {
      pragmaticCreateFRB,
      gameList: structuredClone(games.gameList),
      player: playerInfo
    }
  };
}

export async function addPlayersSequential({ campaignId, players, addPlayers, onTick }) {
    const total = Array.isArray(players) ? players.length : 0;
    let done = 0, errors = 0;
    const nextFrame = () => new Promise(requestAnimationFrame);
    const payload = players.map(pl => ({
        bonusCode: pl.bonusCode || campaignId,
        playerId: pl.id || pl.playerId,
        playerName: pl.label || pl.playerName || String(pl.id || pl.playerId),
        playerStatus: 1
    }))
    let ok = true;
    try {
        await addPlayers(campaignId, payload);
    } catch {
        errors += 1; ok = false;
    } finally {
        done += 1;
        onTick && onTick({ done, total, errors, payload, ok });
        if (onTick) {
            const ret = onTick({ done, total, errors, payload, ok });
            if (ret && typeof ret.then === "function") {
                try { await ret; } catch {}
            }
        }
        await nextFrame();
    }
    return { done, total, errors };
}
